import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { MapContainer, TileLayer, CircleMarker, Tooltip as LTooltip, GeoJSON } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import api from "../lib/api";
import { useLang } from "../lib/i18n";
import { PageHeader, num } from "../lib/ui";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { LayoutGrid, FileDown } from "lucide-react";
import provincesGeo from "../data/drc_provinces.json";

const TONES = ["#F05A28", "#0284C7", "#7C3AED", "#B45309", "#0E7490", "#BE185D"];
const CHORO = ["#FEF3C7", "#FCD34D", "#F59E0B", "#EA580C", "#B91C1C"];
const choroColor = (r) => (r <= 0 ? "#E2E8F0" : r > 0.8 ? CHORO[4] : r > 0.6 ? CHORO[3] : r > 0.4 ? CHORO[2] : r > 0.2 ? CHORO[1] : CHORO[0]);

function downloadMultiPillarPdf(range, lang) {
  const token = localStorage.getItem("token");
  const qs = new URLSearchParams(Object.fromEntries(Object.entries({ ...range, lang }).filter(([, v]) => v))).toString();
  fetch(`${process.env.REACT_APP_BACKEND_URL}/api/multi-pillar/pdf?${qs}`, { headers: { Authorization: `Bearer ${token}` } })
    .then((r) => r.blob()).then((b) => {
      const url = URL.createObjectURL(b);
      const a = document.createElement("a");
      a.href = url; a.download = "Multi_Piliers.pdf"; a.click();
      URL.revokeObjectURL(url);
    });
}

export default function MultiPillar() {
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const [range, setRange] = useState({ date_from: "", date_to: "" });
  const [mapPillar, setMapPillar] = useState("surveillance");
  const [mapMetric, setMapMetric] = useState(null);
  const { data, isLoading } = useQuery({
    queryKey: ["multi-pillar", range, lang],
    queryFn: async () => (await api.get("/multi-pillar", { params: { ...range, lang } })).data,
  });
  const { data: mapData } = useQuery({
    queryKey: ["mp-map", mapPillar, mapMetric, range, lang],
    queryFn: async () => (await api.get("/multi-pillar/map-data", { params: { pillar: mapPillar, metric: mapMetric || undefined, ...range, lang } })).data,
  });

  const maxByCol = {};
  (data?.columns || []).forEach((c) => {
    maxByCol[c.code] = Math.max(1, ...(data?.rows || []).map((r) => r.cells[c.code] || 0));
  });
  const zones = mapData?.zones || [];
  const maxV = Math.max(1, ...zones.map((z) => z.value || 0));
  const pillars = mapData?.pillars || [];
  const curMetrics = pillars.find((p) => p.code === mapPillar)?.metrics || [];

  // province-level aggregation for the choropleth
  const provVals = {};
  zones.forEach((z) => { provVals[z.province] = (provVals[z.province] || 0) + (z.value || 0); });
  const maxProv = Math.max(1, ...Object.values(provVals));

  const styleFeature = (f) => {
    const v = provVals[f.properties.name] || 0;
    return { fillColor: choroColor(v / maxProv), weight: 1.2, color: "#ffffff", fillOpacity: v ? 0.8 : 0.3 };
  };
  const onEachFeature = (f, layer) => {
    const v = provVals[f.properties.name] || 0;
    layer.bindTooltip(
      `<div style="font-size:12px"><b>${f.properties.name}</b><br/>${mapData?.metric_name || ""}: <b>${v.toLocaleString("fr-FR")}</b></div>`,
      { sticky: true }
    );
  };

  return (
    <div data-testid="multi-pillar-page">
      <PageHeader title={t("Tableau de Bord Multi-Piliers")} subtitle={t("Indicateurs clés des piliers par province, d'un seul coup d'œil")}>
        <Button size="sm" className="bg-[#F05A28] hover:bg-[#D84A1B]" data-testid="mp-download-pdf" onClick={() => downloadMultiPillarPdf(range, lang)}>
          <FileDown className="h-4 w-4 mr-1" /> {t("Télécharger PDF")}
        </Button>
      </PageHeader>

      <div className="bg-white border border-slate-200 rounded-md p-4 mb-5 shadow-sm flex flex-wrap gap-3 items-end">
        <div><Label className="text-xs text-slate-500">{t("Du")}</Label><Input type="date" value={range.date_from} onChange={(e) => setRange({ ...range, date_from: e.target.value })} data-testid="mp-from" /></div>
        <div><Label className="text-xs text-slate-500">{t("Au")}</Label><Input type="date" value={range.date_to} onChange={(e) => setRange({ ...range, date_to: e.target.value })} data-testid="mp-to" /></div>
      </div>

      {isLoading ? <p className="text-slate-400 text-sm">{t("Chargement…")}</p> : data && (
        <div className="bg-white border border-slate-200 rounded-md shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="sticky left-0 bg-slate-50">{t("Province")}</TableHead>
                  {data.columns.map((c, i) => (
                    <TableHead key={c.code} className="text-right whitespace-nowrap">
                      <span className="block font-semibold text-slate-700" style={{ color: TONES[i % TONES.length] }}>{c.pillar}</span>
                      <span className="block text-[11px] font-normal text-slate-400">{c.label}</span>
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.rows.map((r) => (
                  <TableRow key={r.province} className="hover:bg-slate-50" data-testid={`mp-row-${r.province}`}>
                    <TableCell className="font-medium sticky left-0 bg-white">{r.province}</TableCell>
                    {data.columns.map((c, i) => {
                      const v = r.cells[c.code] || 0;
                      const ratio = v / maxByCol[c.code];
                      const clickable = c.code !== "surveillance" && c.code !== "laboratory";
                      return (
                        <TableCell key={c.code} className="text-right font-mono text-sm"
                          onClick={() => clickable && navigate(`/pillar/${c.code}`)}
                          style={{ cursor: clickable ? "pointer" : "default", backgroundColor: v ? `${TONES[i % TONES.length]}${Math.round(ratio * 40 + 8).toString(16).padStart(2, "0")}` : "transparent" }}>
                          {num(v)}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <p className="text-xs text-slate-500 p-3 flex items-center gap-1.5"><LayoutGrid className="h-3.5 w-3.5" /> {t("Cliquez une cellule de pilier pour ouvrir son tableau de bord détaillé.")}</p>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-md shadow-sm mt-5 p-5" data-testid="mp-map-card">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <h3 className="text-sm font-semibold text-slate-800">{t("Carte choroplèthe des piliers")}</h3>
          <div className="flex gap-2">
            <Select value={mapPillar} onValueChange={(v) => { setMapPillar(v); setMapMetric(null); }}>
              <SelectTrigger className="h-8 text-xs w-44" data-testid="mp-map-pillar"><SelectValue placeholder={t("Pilier")} /></SelectTrigger>
              <SelectContent>{pillars.map((p) => <SelectItem key={p.code} value={p.code}>{p.label}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={mapMetric || (mapData?.metric || "")} onValueChange={setMapMetric}>
              <SelectTrigger className="h-8 text-xs w-48" data-testid="mp-map-metric"><SelectValue placeholder={t("Indicateur cartographié")} /></SelectTrigger>
              <SelectContent>{curMetrics.map((m) => <SelectItem key={m.code} value={m.code}>{m.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <div className="rounded-lg overflow-hidden" style={{ height: 440 }} data-testid="mp-map">
          <MapContainer center={[-2.2, 28.5]} zoom={7} style={{ height: "100%", width: "100%" }} scrollWheelZoom>
            <TileLayer attribution="&copy; OpenStreetMap" url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
            <GeoJSON key={`${mapPillar}-${mapData?.metric}-${zones.length}`} data={provincesGeo} style={styleFeature} onEachFeature={onEachFeature} />
            {zones.map((z) => (
              <CircleMarker key={z.health_zone} center={[z.lat, z.lng]} radius={4 + (z.value / maxV) * 10}
                pathOptions={{ color: "#0f172a", weight: 1, fillColor: "#0f172a", fillOpacity: 0.55 }}>
                <LTooltip><div className="text-xs"><p className="font-semibold">{z.health_zone} <span className="text-slate-400">({z.province})</span></p>
                  <p>{mapData?.metric_name}: <b>{num(z.value)}</b> · {t("Rapports")}: {z.reports}</p></div></LTooltip>
              </CircleMarker>
            ))}
          </MapContainer>
        </div>
        <div className="flex items-center gap-4 mt-3 flex-wrap">
          <span className="text-xs text-slate-500">{t("Provinces colorées selon l'indicateur")} :</span>
          <div className="flex items-center gap-1">
            <span className="text-[10px] text-slate-400">{t("Faible")}</span>
            {CHORO.map((c) => <span key={c} className="h-3 w-6 rounded-sm" style={{ background: c }} />)}
            <span className="text-[10px] text-slate-400">{t("Élevé")}</span>
          </div>
          <span className="text-xs text-slate-500">· {t("Les cercles indiquent le volume par Zone de Santé")}</span>
        </div>
      </div>
    </div>
  );
}
